import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Copy, ArrowRight, Mail, MessageCircle, Send, Camera, ShieldCheck, TrendingUp, AlertTriangle, Clock, Sparkles, BookOpen } from 'lucide-react';
import confetti from 'canvas-confetti';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { fbTrack } from '../utils/pixel';
import { toast } from 'sonner';

// ── Helpers ──────────────────────────────────────────────────────
const fireConfetti = () => {
  const duration = 2200;
  const end = Date.now() + duration;
  const colors = ['#00D4FF', '#7B61FF', '#A78BFA', '#FFB347', '#22C55E'];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 70, origin: { x: 0, y: 0.85 }, colors, scalar: 1.05 });
    confetti({ particleCount: 4, angle: 120, spread: 70, origin: { x: 1, y: 0.85 }, colors, scalar: 1.05 });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  // Burst
  confetti({ particleCount: 120, spread: 100, origin: { y: 0.4 }, colors });
};

// ── Sub-components ───────────────────────────────────────────────
const StatPill = ({ label, value, mono = true, testId }) => (
  <div className="flex flex-col gap-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl backdrop-blur-md" data-testid={testId}>
    <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</span>
    <span className={`text-sm font-bold text-white ${mono ? 'font-mono' : ''}`}>{value}</span>
  </div>
);

const TimelineStep = ({ number, title, subtitle, status, icon: Icon }) => {
  const isDone = status === 'done';
  const isActive = status === 'active';
  return (
    <div className="flex items-start gap-4">
      <div className="flex flex-col items-center">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${isDone ? 'bg-brand-positive text-black shadow-[0_0_25px_rgba(34,197,94,0.5)]' : isActive ? 'bg-brand-primary/20 border-2 border-brand-primary text-brand-primary' : 'bg-white/5 border border-white/10 text-zinc-500'}`}>
          {isDone ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
          {isActive && <span className="absolute w-11 h-11 rounded-full border-2 border-brand-primary animate-ping opacity-40" />}
        </div>
        {number < 3 && <div className={`w-px flex-1 my-1 min-h-[40px] ${isDone ? 'bg-brand-positive/40' : 'bg-white/10'}`} />}
      </div>
      <div className="flex-1 pb-8">
        <h4 className={`text-base font-semibold ${isDone || isActive ? 'text-white' : 'text-zinc-400'}`}>{title}</h4>
        <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>
      </div>
    </div>
  );
};

const RuleCard = ({ icon: Icon, label, value, color }) => (
  <motion.div
    whileHover={{ y: -4, transition: { duration: 0.2 } }}
    className={`relative p-5 bg-[#0F1420] border border-white/5 rounded-2xl group overflow-hidden hover:border-${color}/30 transition-all`}
  >
    <div className={`w-10 h-10 rounded-xl bg-${color}/10 border border-${color}/30 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
      <Icon className={`w-5 h-5 text-${color}`} />
    </div>
    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</div>
    <div className="text-xl font-bold text-white mt-1 font-mono">{value}</div>
    <div className={`absolute -bottom-12 -right-12 w-32 h-32 bg-${color}/8 rounded-full blur-3xl group-hover:bg-${color}/15 transition-colors`} />
  </motion.div>
);

const SocialButton = ({ icon: Icon, label, color, href }) => (
  <a href={href} target="_blank" rel="noreferrer" className={`flex items-center gap-3 px-5 py-3 bg-white/5 border border-white/10 rounded-xl hover:bg-${color}/15 hover:border-${color}/40 transition-all group`}>
    <Icon className={`w-5 h-5 text-${color} group-hover:scale-110 transition-transform`} />
    <span className="text-sm font-semibold text-white">{label}</span>
  </a>
);

// ── Page ─────────────────────────────────────────────────────────
const Success = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const orderId = searchParams.get('order') || '';
  const isPending = searchParams.get('pending') === '1';
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const firedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (!orderId) {
      setLoading(false);
      return;
    }
    api.get(`/orders/${orderId}`)
      .then(r => { if (!cancelled) setOrder(r.data); })
      .catch(() => { if (!cancelled) setOrder(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orderId]);

  // Confetti + Pixel Purchase tracking on first load (skip for pending state)
  useEffect(() => {
    if (firedRef.current) return;
    if (isPending) { firedRef.current = true; return; }
    firedRef.current = true;
    setTimeout(fireConfetti, 350);
    fbTrack('Purchase', {
      content_ids: order?.plan_id ? [order.plan_id] : undefined,
      content_name: order?.plan_name,
      value: order?.amount,
      currency: 'USD',
      content_type: 'product',
    });
  }, [order, isPending]);

  const copyOrder = () => {
    if (!orderId) return;
    navigator.clipboard.writeText(orderId).then(() => toast.success('Order ID copied'));
  };

  const challengeLabel = order?.challenge_type === 'instant' ? 'Instant Funding' : order?.challenge_type === '1-step' ? '1-Step Challenge' : order?.challenge_type === '2-step' ? '2-Step Challenge' : (order?.plan_name || '—');
  const accountSize = order?.account_size ? `$${Number(order.account_size).toLocaleString()}` : '—';
  const emailAddr = order?.email || user?.email || '—';
  const purchaseTime = order?.created_at ? new Date(order.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

  // Rules per challenge type (defaults — will reflect real rules from /trading-rules eventually)
  const rules = order?.challenge_type === 'instant'
    ? { dailyDD: '4%', maxDD: '7%', target: 'None', period: 'Unlimited' }
    : order?.challenge_type === '1-step'
    ? { dailyDD: '3%', maxDD: '6%', target: '9%', period: 'Unlimited' }
    : { dailyDD: '3%', maxDD: '6%', target: '6% + 6%', period: 'Unlimited' };

  return (
    <div className="relative min-h-screen bg-[#050505] text-white overflow-hidden">
      {/* Floating background gradients */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] bg-brand-primary/15 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '6s' }} />
        <div className="absolute top-1/3 -right-40 w-[480px] h-[480px] bg-[#7B61FF]/15 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute bottom-0 left-1/3 w-[420px] h-[420px] bg-brand-positive/8 rounded-full blur-[120px]" />
      </div>

      <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-10 md:py-14">

        {/* ── HERO ── */}
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-12 md:mb-16" data-testid="success-hero">
          {/* Animated checkmark / pending */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
            className="relative inline-flex items-center justify-center w-24 h-24 md:w-28 md:h-28 mb-6"
          >
            <div className={`absolute inset-0 rounded-full ${isPending ? 'bg-yellow-400/20' : 'bg-brand-positive/20'} animate-ping`} style={{ animationDuration: '2s' }} />
            <div className={`absolute inset-2 rounded-full ${isPending ? 'bg-yellow-400/30' : 'bg-brand-positive/30'} animate-pulse`} />
            <div className={`relative w-full h-full rounded-full bg-gradient-to-br ${isPending ? 'from-yellow-400 to-orange-500 shadow-[0_0_60px_rgba(250,204,21,0.55)]' : 'from-brand-positive to-emerald-600 shadow-[0_0_60px_rgba(34,197,94,0.55)]'} flex items-center justify-center`}>
              {isPending ? <Clock className="w-12 h-12 md:w-14 md:h-14 text-black" strokeWidth={2.5} /> : <CheckCircle2 className="w-12 h-12 md:w-14 md:h-14 text-black" strokeWidth={2.5} />}
            </div>
          </motion.div>

          <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight" data-testid="success-headline">
            {isPending ? 'Payment Submitted ⏳' : 'Payment Successful 🎉'}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
            className="text-lg md:text-2xl mt-3 bg-gradient-to-r from-brand-primary via-[#A78BFA] to-brand-primary bg-clip-text text-transparent font-bold">
            {isPending ? 'We are verifying your UPI payment.' : 'Welcome to AYRONN Funding.'}
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
            className="text-sm md:text-base text-zinc-400 mt-3">
            {isPending
              ? 'Our team manually verifies AYRONN PAY submissions. You will receive an email + your trading account once approved (usually within a few hours).'
              : 'Your challenge account is being prepared.'}
          </motion.p>

          {/* Order detail pills */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8 max-w-3xl mx-auto">
            {loading ? (
              [...Array(4)].map((_, i) => <div key={i} className="h-[70px] bg-white/5 rounded-xl animate-pulse" />)
            ) : (
              <>
                <StatPill label="Challenge" value={challengeLabel} mono={false} testId="stat-challenge" />
                <StatPill label="Account Size" value={accountSize} testId="stat-size" />
                <StatPill label="Email" value={emailAddr} mono={false} testId="stat-email" />
                <div className="flex flex-col gap-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl backdrop-blur-md">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Order ID</span>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold text-white font-mono truncate" data-testid="stat-order-id">{orderId || '—'}</span>
                    {orderId && (
                      <button onClick={copyOrder} className="p-1 hover:bg-white/10 rounded text-zinc-400 hover:text-white" data-testid="copy-order-btn"><Copy className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </div>
              </>
            )}
          </motion.div>

          <p className="text-[11px] text-zinc-600 mt-3">Purchased on {purchaseTime}</p>

          {/* CTAs */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}
            className="flex flex-col sm:flex-row gap-3 justify-center items-stretch mt-8">
            <Link to="/dashboard"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-brand-primary text-black font-bold rounded-xl hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(0,212,255,0.55)] transition-all"
              data-testid="success-cta-dashboard"
              onClick={() => fbTrack('Lead', { content_name: 'success_to_dashboard' })}>
              Go To Dashboard <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="https://discord.gg/" target="_blank" rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/5 border border-white/10 text-white font-semibold rounded-xl hover:bg-white/10 hover:border-[#5865F2]/40 transition-all"
              data-testid="success-cta-discord">
              <MessageCircle className="w-4 h-4 text-[#5865F2]" /> Join Discord Community
            </a>
          </motion.div>
        </motion.section>

        {/* ── WHAT HAPPENS NEXT ── */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-white/[0.02] border border-white/5 rounded-3xl p-6 md:p-10 backdrop-blur-md mb-12">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-5 h-5 text-brand-primary" />
            <h2 className="text-xl md:text-2xl font-bold">What happens next</h2>
          </div>
          <p className="text-sm text-zinc-500 mb-8">Estimated delivery: <span className="text-brand-primary font-semibold">a few minutes</span> · You'll receive an email instantly.</p>
          <div className="max-w-2xl">
            <TimelineStep number={1} title="Payment Verified" subtitle="Your transaction has been confirmed on the blockchain." icon={CheckCircle2} status="done" />
            <TimelineStep number={2} title="Challenge Credentials Generated" subtitle="Our team is provisioning your trading account on Exness MT5." icon={Clock} status="active" />
            <TimelineStep number={3} title="Credentials Sent To Your Email" subtitle="Login ID, password, server, and platform details delivered." icon={Mail} status="pending" />
          </div>
        </motion.section>

        {/* ── IMPORTANT RULES ── */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <BookOpen className="w-5 h-5 text-[#A78BFA]" />
                <h2 className="text-xl md:text-2xl font-bold">Your Trading Rules</h2>
              </div>
              <p className="text-sm text-zinc-500">Follow these to stay funded and unlock daily payouts.</p>
            </div>
            <Link to="/rules" className="hidden sm:inline-flex items-center gap-1 text-xs text-brand-primary hover:underline">View full rules <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <RuleCard icon={AlertTriangle} label="Daily Drawdown" value={rules.dailyDD} color="yellow-400" />
            <RuleCard icon={ShieldCheck} label="Max Drawdown" value={rules.maxDD} color="red-400" />
            <RuleCard icon={TrendingUp} label="Profit Target" value={rules.target} color="brand-primary" />
            <RuleCard icon={Clock} label="Trading Period" value={rules.period} color="brand-positive" />
          </div>
        </motion.section>

        {/* ── COMMUNITY ── */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="relative bg-gradient-to-br from-brand-primary/[0.06] via-[#7B61FF]/[0.04] to-transparent border border-white/5 rounded-3xl p-6 md:p-10 mb-12 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary/10 rounded-full blur-[80px]" />
          <div className="relative">
            <h2 className="text-xl md:text-2xl font-bold mb-2">Join Thousands of Traders Building Consistency with AYRONN Funding.</h2>
            <p className="text-sm text-zinc-400 mb-6">Connect with our community of funded traders. Share strategies, get insights, win challenges.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SocialButton icon={MessageCircle} label="Join Discord" color="[#5865F2]" href="https://discord.gg/" />
              <SocialButton icon={Send} label="Telegram Channel" color="[#0088CC]" href="https://t.me/" />
              <SocialButton icon={Camera} label="Follow on Instagram" color="[#E4405F]" href="https://instagram.com/" />
            </div>
          </div>
        </motion.section>

        {/* ── SUPPORT ── */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center pb-10">
          <h3 className="text-base md:text-lg font-bold text-white">Need Help?</h3>
          <p className="text-sm text-zinc-400 mt-1">Our team responds within 24 hours.</p>
          <a href="mailto:ayronnsupport@gmail.com" className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-white/5 border border-white/10 text-white text-sm font-mono rounded-lg hover:bg-brand-primary hover:text-black hover:border-brand-primary transition-all" data-testid="support-email-btn">
            <Mail className="w-4 h-4" /> ayronnsupport@gmail.com
          </a>
        </motion.section>

      </div>
    </div>
  );
};

export default Success;
